/**
 * PropertyDetailsPanel Component
 * 
 * Displays all property details in a read/edit view for the split panel layout.
 * Shows property information and allows editing via modal.
 */

import React from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { ArtifactProperty, ArtifactItemState } from '../../types/artifactItems';

const { Title, Text } = Typography;

interface PropertyDetailsPanelProps {
  property: ArtifactProperty | null;
  loading?: boolean;
  artifactStates?: ArtifactItemState[];
  onEdit?: (property: ArtifactProperty) => void;
  onViewGlobalProperty?: (propertyKey: string) => void;
  globalVisibilityProperties?: Record<string, any>;
}

export const PropertyDetailsPanel: React.FC<PropertyDetailsPanelProps> = ({
  property,
  loading = false,
  artifactStates = [],
  onEdit,
  onViewGlobalProperty,
  globalVisibilityProperties = {},
}) => {
  if (loading) {
    return (
      <Card>
        <Spin spinning={loading} />
      </Card>
    );
  }

  if (!property) {
    return (
      <Card>
        <Empty
          description={
            <Text type="secondary">Select a property from the list to view its details</Text>
          }
        />
      </Card>
    );
  }

  // Extract state mappings
  const createdStates = (property as any).state_mappings
    ?.filter((m: any) => m.mapping_type === 'created')
    .map((m: any) => m.state_name) || [];
  const updatedStates = (property as any).state_mappings
    ?.filter((m: any) => m.mapping_type === 'updated')
    .map((m: any) => m.state_name) || [];

  const isGlobalProperty = globalVisibilityProperties[property.property_key];

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Property Details
          </Title>
          {isGlobalProperty && (
            <Tag color="green" title="Global Visibility Property">
              Global
            </Tag>
          )}
        </Space>
      }
      extra={
        <Space>
          {onViewGlobalProperty && (
            <Button
              type="link"
              size="small"
              onClick={() => onViewGlobalProperty(property.property_key)}
            >
              View Global Property
            </Button>
          )}
          {onEdit && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit(property)}
            >
              Edit
            </Button>
          )}
        </Space>
      }
      className="property-details-card"
    >
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Property Key">
          <Text strong style={{ fontFamily: 'monospace' }}>
            {property.property_key}
          </Text>
        </Descriptions.Item>

        <Descriptions.Item label="Type">
          <Tag style={{ fontFamily: 'monospace' }}>{property.property_type}</Tag>
        </Descriptions.Item>

        <Descriptions.Item label="Description">
          {property.description || <Text type="secondary">No description</Text>}
        </Descriptions.Item>

        <Descriptions.Item label="Required">
          {property.is_required ? (
            <Tag color="red">Required</Tag>
          ) : (
            <Tag>Optional</Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Default Value">
          {property.default_value ? (
            <Text code>{property.default_value}</Text>
          ) : (
            <Text type="secondary">No default value</Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Input Source">
          {property.input_source ? (
            <Tag>{property.input_source}</Tag>
          ) : (
            <Text type="secondary">Not specified</Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Property Scope">
          {property.property_scope ? (
            <Tag>{property.property_scope}</Tag>
          ) : (
            <Text type="secondary">Not specified</Text>
          )}
        </Descriptions.Item>

        {property.input_label && (
          <Descriptions.Item label="Input Label">
            {property.input_label}
          </Descriptions.Item>
        )}

        {property.input_type && (
          <Descriptions.Item label="Input Type">
            <Tag>{property.input_type}</Tag>
          </Descriptions.Item>
        )}

        {property.global_option_catalog_ref && (
          <Descriptions.Item label="Option Catalog">
            <Space>
              <Text>{property.global_option_catalog_name || property.global_option_catalog_ref}</Text>
              <Tag color="blue">Catalog Reference</Tag>
            </Space>
          </Descriptions.Item>
        )}

        {property.is_child_reference !== undefined && (
          <Descriptions.Item label="Child Reference">
            {property.is_child_reference ? (
              <Tag color="orange">Yes - Requires creation</Tag>
            ) : (
              <Tag>No - Reference only</Tag>
            )}
          </Descriptions.Item>
        )}

        {createdStates.length > 0 && (
          <Descriptions.Item label="Created In States">
            <Space wrap>
              {createdStates.map((stateName: string) => (
                <Tag key={stateName} color="green">
                  {stateName}
                </Tag>
              ))}
            </Space>
          </Descriptions.Item>
        )}

        {updatedStates.length > 0 && (
          <Descriptions.Item label="Updated In States">
            <Space wrap>
              {updatedStates.map((stateName: string) => (
                <Tag key={stateName} color="blue">
                  {stateName}
                </Tag>
              ))}
            </Space>
          </Descriptions.Item>
        )}

        {property.validation_rules && Object.keys(property.validation_rules).length > 0 && (
          <Descriptions.Item label="Validation Rules">
            <Text code style={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(property.validation_rules, null, 2)}
            </Text>
          </Descriptions.Item>
        )}

        {property.referenced_artifact_type_ids && property.referenced_artifact_type_ids.length > 0 && (
          <Descriptions.Item label="Referenced Artifact Types">
            <Space wrap>
              {property.referenced_artifact_type_ids.map((typeId: string) => (
                <Tag key={typeId}>{typeId}</Tag>
              ))}
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};
